import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DayMap from './DayMap';
import { safeFormatDate, formatDateWithLocale } from '../shared/utils';
import { activityMatchesOption } from '../shared/activityUtils';

export default function DayMapModal({ show, onHide, day, dayIndex, continuingAccommodations = [], planId }) {
  const { t } = useTranslation();
  const [mapLocations, setMapLocations] = useState([]);
  const [mapKey, setMapKey] = useState(Date.now());
  // Option filter state: 'all' | 1 | 2 | 3 (default is 1)
  const [selectedOption, setSelectedOption] = useState(1);

  // Get the set of options that exist in the day's activities
  const getAvailableOptions = () => {
    if (!day || !day.activities || day.activities.length === 0) return new Set();
    
    const availableOptions = new Set();
    
    day.activities.forEach(activity => {
      const options = activity.option;
      
      // If activity has specific options (not empty/null/"all")
      if (options && options !== 'all') {
        if (Array.isArray(options) && options.length > 0) {
          options.forEach(opt => availableOptions.add(opt));
        } else if (typeof options === 'number') {
          availableOptions.add(options);
        }
      }
    });
    
    return availableOptions;
  };

  // Check if day has activities with specific options (not all empty/null)
  const hasActivitiesWithOptions = () => {
    return getAvailableOptions().size > 0;
  };

  // Reset map key when modal opens to force fresh mount
  useEffect(() => {
    if (show) {
      setMapKey(Date.now());
    }
  }, [show]);

  // Extract activities from day and convert to map format
  useEffect(() => {
    if (!day || !show) {
      setMapLocations([]);
      return;
    }

    const ongoingAccommodationsData = []; // For ongoing accommodations (to be placed first)
    const regularActivitiesData = []; // For regular activities
    const activityIds = new Set(); // Track activity IDs to avoid duplicates
    const continuingAccomIds = new Set(); // Track IDs of continuing accommodations

    // First, collect IDs of continuing accommodations
    continuingAccommodations.forEach(accom => {
      if (accom.id) {
        continuingAccomIds.add(accom.id);
      }
    });

    // Add continuing accommodations first (ongoing accommodations)
    // Note: Continuing accommodations are always shown (they belong to all options)
    continuingAccommodations.forEach(accom => {
      // Only add if has location data
      if (accom.location && typeof accom.location === 'object' && accom.location.latitude && accom.location.longitude) {
        const address = typeof accom.location === 'object' ? accom.location.address : accom.location;
        
        ongoingAccommodationsData.push({
          name: accom.name,
          type: 'accommodation',
          day: dayIndex + 1,
          date: day.date ? safeFormatDate(day.date) : '',
          address: address,
          time: '00:00', // Set time to 00:00 for ongoing accommodations
          isOngoing: true, // Flag to distinguish ongoing accommodations
          gps: {
            latitude: Number(accom.location.latitude),
            longitude: Number(accom.location.longitude)
          }
        });
        
        if (accom.id) {
          activityIds.add(accom.id);
        }
      }
    });

    // Process activities from the day's activities array
    if (day.activities) {
      day.activities.forEach(activity => {
        // Filter by selected option
        if (!activityMatchesOption(activity, selectedOption)) {
          return; // Skip activities that don't match the filter
        }
        
        if (activity.id) {
          activityIds.add(activity.id);
        }

        // Skip if this is a continuing accommodation (already added above)
        if (continuingAccomIds.has(activity.id) && activity.type === 'accommodation') {
          return;
        }

        // Get address
        let address = '';
        if (activity.location) {
          address = typeof activity.location === 'object' ? activity.location.address : activity.location;
        }

        // For transportation, show from and to
        if (activity.type === 'transportation') {
          let fromAddress = '';
          let toAddress = '';

          if (activity.fromLocation) {
            fromAddress = typeof activity.fromLocation === 'object' ? activity.fromLocation.address : activity.fromLocation;
          }
          if (activity.toLocation) {
            toAddress = typeof activity.toLocation === 'object' ? activity.toLocation.address : activity.toLocation;
          }

          if (fromAddress && toAddress) {
            address = `${fromAddress} → ${toAddress}`;
          } else if (fromAddress) {
            address = `From: ${fromAddress}`;
          } else if (toAddress) {
            address = `To: ${toAddress}`;
          }
        }

        // Add to map data if coordinates exist
        if (activity.location && typeof activity.location === 'object' && activity.location.latitude && activity.location.longitude) {
          // Extract time from startDateTime for badge display (format: HH:mm)
          const activityTime = activity.startDateTime ? safeFormatDate(activity.startDateTime, 'HH:mm') : null;
          
          regularActivitiesData.push({
            name: activity.name,
            type: activity.type || 'activity',
            day: dayIndex + 1,
            date: day.date ? safeFormatDate(day.date) : '',
            address: address,
            time: activityTime, // Add time for badge display
            isOngoing: false, // Regular activity (not ongoing)
            gps: {
              latitude: Number(activity.location.latitude),
              longitude: Number(activity.location.longitude)
            }
          });
        }

        // For transportation activities, add both fromLocation and toLocation if they have coordinates
        if (activity.type === 'transportation') {
          const departureTime = activity.startDateTime ? safeFormatDate(activity.startDateTime, 'HH:mm') : null;
          const arrivalTime = activity.endDateTime ? safeFormatDate(activity.endDateTime, 'HH:mm') : null;
          
          if (activity.fromLocation && typeof activity.fromLocation === 'object' && activity.fromLocation.latitude && activity.fromLocation.longitude) {
            regularActivitiesData.push({
              name: `${activity.name} (From)`,
              type: activity.type || 'activity',
              day: dayIndex + 1,
              date: day.date ? safeFormatDate(day.date) : '',
              address: typeof activity.fromLocation === 'object' ? activity.fromLocation.address : activity.fromLocation,
              time: departureTime, // Use departure time for "From" location
              isOngoing: false,
              gps: {
                latitude: Number(activity.fromLocation.latitude),
                longitude: Number(activity.fromLocation.longitude)
              }
            });
          }

          if (activity.toLocation && typeof activity.toLocation === 'object' && activity.toLocation.latitude && activity.toLocation.longitude) {
            regularActivitiesData.push({
              name: `${activity.name} (To)`,
              type: activity.type || 'activity',
              day: dayIndex + 1,
              date: day.date ? safeFormatDate(day.date) : '',
              address: typeof activity.toLocation === 'object' ? activity.toLocation.address : activity.toLocation,
              time: arrivalTime, // Use arrival time (endDateTime) for "To" location
              isOngoing: false,
              gps: {
                latitude: Number(activity.toLocation.latitude),
                longitude: Number(activity.toLocation.longitude)
              }
            });
          }
        }
      });
    }

    // Combine: ongoing accommodations first, then regular activities
    const mapData = [...ongoingAccommodationsData, ...regularActivitiesData];

    setMapLocations(mapData);
  }, [day, dayIndex, continuingAccommodations, show, selectedOption]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="lg-down" className="day-map-modal">
      <Modal.Header closeButton className="day-map-modal-header">
        <div className="d-flex flex-column w-100">
          <div className="d-flex justify-content-between align-items-center mb-2" style={{ paddingRight: '40px' }}>
            <Modal.Title className="day-map-modal-title mb-0">
              <h6 className="mb-0">
                <i className="bi bi-calendar-date me-1"></i>
                {day?.date ? (
                  `${t('planDetail.days.dayMap.day')} ${dayIndex + 1}: ${formatDateWithLocale(day.date, 'PPP')}`
                ) : (
                  `${t('planDetail.days.dayMap.day')} ${dayIndex + 1}`
                )}
              </h6>
            </Modal.Title>
            {/* Option Filter Dropdown - only show if activities have specific options */}
            {hasActivitiesWithOptions() && (() => {
              const availableOptions = getAvailableOptions();
              const sortedOptions = Array.from(availableOptions).sort((a, b) => a - b);
              
              return (
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <i className="bi bi-funnel me-1"></i>
                    {selectedOption === 'all' ? t('planDetail.days.dayMap.allOptions') : `${t('planDetail.days.dayMap.option')} ${selectedOption}`}
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item
                      onClick={() => setSelectedOption('all')}
                      active={selectedOption === 'all'}
                    >
                      <i className="bi bi-list-ul me-2"></i>
                      {t('planDetail.days.dayMap.allOptions')}
                    </Dropdown.Item>
                    {sortedOptions.map(opt => (
                      <Dropdown.Item
                        key={opt}
                        onClick={() => setSelectedOption(opt)}
                        active={selectedOption === opt}
                      >
                        <i className={`bi bi-${opt}-circle me-2`}></i>
                        {t('planDetail.days.dayMap.option')} {opt}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              );
            })()}
          </div>
          {/* Legend */}
          <div className="d-flex align-items-center gap-2 mt-2">
            <div className="d-flex gap-3">
              <div className="d-flex align-items-center">
                <span 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#c33',
                    display: 'inline-block',
                    marginRight: '4px'
                  }}
                ></span>
                <small className="text-muted">{t('planDetail.days.dayMap.attraction')}</small>
              </div>
              <div className="d-flex align-items-center">
                <span 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#2ecc71',
                    display: 'inline-block',
                    marginRight: '4px'
                  }}
                ></span>
                <small className="text-muted">{t('planDetail.days.dayMap.transport')}</small>
              </div>
              <div className="d-flex align-items-center">
                <span 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#3388ff',
                    display: 'inline-block',
                    marginRight: '4px'
                  }}
                ></span>
                <small className="text-muted">{t('planDetail.days.dayMap.hotel')}</small>
              </div>
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: 0, minHeight: '400px' }}>
        {show && (
          <div style={{ height: '70vh', width: '100%' }}>
            {mapLocations.length > 0 ? (
              <DayMap 
                key={mapKey}
                locations={mapLocations} 
                height="100%" 
                planId={planId}
                dayIndex={dayIndex}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100 bg-light">
                <div className="text-center text-muted">
                  <i className="bi bi-map display-4 mb-3"></i>
                  <p>{t('planDetail.days.dayMap.noActivitiesWithLocation')}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="day-map-modal-footer">
        <Button variant="primary" onClick={onHide}>
          {t('planDetail.days.dayMap.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

